import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from '@/i18n';
import { SlideOver, SlideOverFooter } from '@/components/Shared/SlideOver';
import { FormField } from '@/components/Shared/FormField';
import { TextArea } from '@/components/Shared/TextArea';
import SearchSelect from '@/components/Shared/SearchSelect';
import { useAuthStore } from '@/store/useAuthStore';
import { useEmployeeStore } from '@/store/useEmployeeStore';
import { useObjectStore } from '@/store/useObjectStore';
import { useOpenItemsStore } from '@/store/useOpenItemsStore';
import type { OpenItemDto } from '@/store/useOpenItemsStore';
import { isReport } from '@/pages/OpenItems/openItemText';

const MAX_TITLE = 200;
const MAX_NOTE = 2000;

interface OpenItemFormProps {
  isOpen: boolean;
  onClose: () => void;
  /** null = create a new manual item. */
  item: OpenItemDto | null;
}

export default function OpenItemForm({ isOpen, onClose, item }: OpenItemFormProps) {
  const { t } = useTranslation(['openItems', 'common']);
  const isAdmin = useAuthStore((s) => s.isAdmin);
  const currentEmployeeId = useAuthStore((s) => s.employee?.id ?? '');
  const employees = useEmployeeStore((s) => s.employees);
  const fetchAllEmployees = useEmployeeStore((s) => s.fetchAllEmployees);
  const createOpenItem = useOpenItemsStore((s) => s.createOpenItem);
  const updateOpenItem = useOpenItemsStore((s) => s.updateOpenItem);
  const saving = useOpenItemsStore((s) => s.saving);
  const objects = useObjectStore((s) => s.objects);
  const fetchAllObjects = useObjectStore((s) => s.fetchAllObjects);

  // A report goes to Verwaltung as a whole: no due date, no assignee, but an object.
  const report = item !== null && isReport(item);

  const [title, setTitle] = useState('');
  const [note, setNote] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [assigneeId, setAssigneeId] = useState('');
  const [objectId, setObjectId] = useState('');
  const [error, setError] = useState<string | undefined>();
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setTitle(item?.title ?? '');
    setNote(item?.note ?? '');
    setDueDate(item?.dueDate ?? '');
    setAssigneeId(item?.assigneeEmployeeId ?? currentEmployeeId);
    setObjectId(item?.objectId ?? '');
    setError(undefined);
    setTouched(false);
  }, [isOpen, item, currentEmployeeId]);

  // Only the administration assigns items to others; employees get no picker at all.
  useEffect(() => {
    if (isOpen && isAdmin && !report) fetchAllEmployees({ status: 'active' });
  }, [isOpen, isAdmin, report, fetchAllEmployees]);

  useEffect(() => {
    if (isOpen && report) fetchAllObjects();
  }, [isOpen, report, fetchAllObjects]);

  const objectOptions = useMemo(
    () => objects.filter((o) => o.id).map((o) => ({ id: o.id ?? '', label: o.name ?? '' })),
    [objects],
  );

  const employeeOptions = useMemo(
    () =>
      employees
        .map((e) => ({ id: e.id ?? '', label: `${e.firstName ?? ''} ${e.lastName ?? ''}`.trim() }))
        .filter((o) => o.id),
    [employees],
  );

  const handleSubmit = async () => {
    const trimmedTitle = title.trim();
    if (!trimmedTitle) {
      setError(t('openItems:validation.titleRequired'));
      return;
    }
    const payload = {
      title: trimmedTitle,
      note: note.trim() || null,
      dueDate: report ? null : dueDate || null,
      // null = server default: the caller on create, the current assignee on update.
      assigneeEmployeeId: !report && isAdmin && assigneeId ? assigneeId : null,
      // Always sent: on update the server reads a missing object as "none", not "keep".
      objectId: objectId || null,
    };
    const saved = item?.id
      ? await updateOpenItem(item.id, payload)
      : await createOpenItem(payload);
    if (saved) onClose();
  };

  const markTouched = () => setTouched(true);

  return (
    <SlideOver
      isOpen={isOpen}
      onClose={onClose}
      title={
        report ? t('openItems:report.edit') : item ? t('openItems:actions.edit') : t('openItems:actions.create')
      }
      size="md"
      hasUnsavedChanges={touched && !saving}
      footer={
        <SlideOverFooter
          onCancel={onClose}
          onConfirm={handleSubmit}
          confirmText={t('common:actions.save')}
          cancelText={t('common:actions.cancel')}
          loading={saving}
        />
      }
    >
      <div className="space-y-5">
        <FormField
          label={t('openItems:fields.title')}
          name="title"
          required
          value={title}
          onChange={(e) => {
            setTitle(e.target.value);
            setError(undefined);
            markTouched();
          }}
          maxLength={MAX_TITLE}
          placeholder={t('openItems:placeholders.title')}
          error={error}
        />
        <TextArea
          label={t('openItems:fields.note')}
          name="note"
          value={note}
          onChange={(e) => {
            setNote(e.target.value);
            markTouched();
          }}
          maxLength={MAX_NOTE}
          rows={4}
          placeholder={t('openItems:placeholders.note')}
        />
        {report ? (
          <SearchSelect
            label={t('openItems:fields.object')}
            options={objectOptions}
            value={objectId}
            onChange={(id) => {
              setObjectId(id);
              markTouched();
            }}
            placeholder={t('openItems:placeholders.object')}
            noResultsText={t('openItems:placeholders.noResults')}
            inlineDropdown
          />
        ) : (
          <FormField
            label={t('openItems:fields.dueDate')}
            name="dueDate"
            type="date"
            className="tabular-nums"
            value={dueDate}
            onChange={(e) => {
              setDueDate(e.target.value);
              markTouched();
            }}
          />
        )}
        {isAdmin && !report && (
          <SearchSelect
            label={t('openItems:fields.assignee')}
            options={employeeOptions}
            value={assigneeId}
            onChange={(id) => {
              setAssigneeId(id);
              markTouched();
            }}
            placeholder={t('openItems:placeholders.assignee')}
            noResultsText={t('openItems:placeholders.noResults')}
          />
        )}
      </div>
    </SlideOver>
  );
}
